import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import PreventOutsideScroll from '@ott/prevent-outside-scroll';
import { l10n } from '@ott/l10n';

import PropTypes from 'prop-types';
import fastdom from 'fastdom';

import cls from 'classnames/bind';
import s from './Options.scss';
const cx = cls.bind(s);

import AirportIcon from '@ott/icon-airport';
import MapPointIcon from '@ott/map-point-icon';
import HotelsIcon from '@ott/icon-hotels';
import Option from '@ott/option';

import _get from 'lodash/get';
import _noop from 'lodash/noop';

class Options extends PureComponent {
  static propTypes = {
    isTouchDevice: PropTypes.bool.isRequired,
    showExtraData: PropTypes.bool,
    showOneLine: PropTypes.bool,
  };

  static defaultProps = {
    showExtraData: true,
    handleClose: _noop,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.setActiveOptionRef = this.setRef.bind(this, 'activeOptionRef');
    this.setOptionsContainerRef = this.setRef.bind(this, 'optionsContainerRef');
  }

  componentDidMount() {
    const { isMobile } = this.props;

    if (!isMobile) {
      this.bindOutsideClicks();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isMobile && !this.props.isMobile) {
      this.unbindOutsideClicks();
    }

    if (!nextProps.isMobile && this.props.isMobile) {
      this.bindOutsideClicks();
    }
  }

  componentDidUpdate() {
    if (this.activeOptionRef && this._activeOptionReveal) {
      this._revealActiveOption();
    }
  }

  componentWillUnmount() {
    fastdom.clear();
    this.unbindOutsideClicks();
  }

  static getValue(item, key) {
    if (!item) {
      return item;
    }
    return _get(item, key);
  }

  setRef = (key, element) => {
    this[key] = element;
  };

  bindOutsideClicks() {
    if (this._boundOutside) {
      return;
    }

    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('touchstart', this.handleClickOutside);

    this._boundOutside = true;
  }

  unbindOutsideClicks() {
    if (!this._boundOutside) {
      return;
    }

    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('touchstart', this.handleClickOutside);

    this._boundOutside = false;
  }

  _revealActiveOption() {
    fastdom.clear();
    fastdom.measure(() => {
      if (!this.activeOptionRef) {
        return;
      }

      const activeOptionDOMNode = findDOMNode(this.activeOptionRef);
      const optionsContainerDOMNode = findDOMNode(this.optionsContainerRef || this);
      const activeOptionRect = activeOptionDOMNode.getBoundingClientRect();
      const optionsContainerRect = optionsContainerDOMNode.getBoundingClientRect();

      // TODO: заменить на scrollIntoView?
      if (
        activeOptionRect.bottom > optionsContainerRect.bottom ||
        activeOptionRect.top < optionsContainerRect.top
      ) {
        const scrollTop =
          activeOptionDOMNode.offsetTop +
          activeOptionDOMNode.offsetHeight -
          optionsContainerDOMNode.offsetHeight;
        fastdom.mutate(() => {
          optionsContainerDOMNode.scrollTop = scrollTop;
        });
      }
    });

    this._activeOptionReveal = false;
  }

  handleBlur = e => {
    if (e.relatedTarget) {
      this.props.handleClose();
    }
  };

  handleClickOutside = e => {
    const domNode = findDOMNode(this);
    if (!domNode || !domNode.contains(e.target)) {
      const { value, valueKey, activeItem } = this.props;
      const activeItemValue = _get(activeItem, valueKey) || '';

      if (value && value.toLowerCase() === activeItemValue.toLowerCase()) {
        this.props.handleOptionSelect(activeItem, { skipFocusNextControl: true });
      }
    }
  };

  // todo подумать о вынесении этой логики в GeoAutocomplete
  // например, в экстранете эти иконки не используются
  mapPointTypes = ['city', 'country', 'geo', 'tourism'];

  renderIcon(item) {
    if (!item || !item.type || this.mapPointTypes.includes(item.type)) {
      return <MapPointIcon className={cx('optionContent__icon')} />;
    }

    if (item.type === 'airport') {
      return <AirportIcon className={cx('optionContent__icon', 'optionContent__icon--centered')} />;
    }

    if (item.type === 'hotel') {
      return (
        <HotelsIcon
          className={cx('optionContent__icon', 'optionContent__icon--centered')}
          fill="#ccc"
        />
      );
    }
  }

  renderOptionContent(item) {
    const { valueKey, activeItem, showExtraData, showOneLine } = this.props;

    let isActive = item === activeItem;
    const name = Options.getValue(item, valueKey) + (item.country ? ',' : '');

    return (
      <div className={cx('optionContent', { 'optionContent--isActive': isActive })}>
        {this.renderIcon(item)}
        <div className={cx('optionContent__container')}>
          <div className={cx('optionContent__wrap')}>
            <div className={cx(showOneLine ? 'optionContent__one-line' : 'optionContent__text')}>
              <span className={cx('optionContent__name')}>{name}&nbsp;</span>
              {item.country && <span className={cx('optionContent__country')}>{item.country}</span>}
            </div>
            {showExtraData && item.code && (
              <span
                className={cx('optionContent__extraData', {
                  'optionContent__extraData--isHidden': !item.code,
                })}
              >
                {item.code}
              </span>
            )}
          </div>
          {!!item.description && (
            <div className={cx('optionContent__description')}>{item.description}</div>
          )}
        </div>
      </div>
    );
  }

  renderOption(item, i) {
    const { activeItem, isTouchDevice } = this.props;

    const isActive = activeItem === item;
    const ref = isActive ? this.setActiveOptionRef : null;

    if (item.isGroup) {
      return (
        <Option
          key={`${i}`}
          className={cx('hover')}
          pureChildren={true}
          withArrow={false}
          withPadding={false}
          isSelected={false}
          isActive={false}
          isGroup={true}
          isTouchDevice={isTouchDevice}
          data-allow-touch-move={true}
        >
          <div className={cx('groupContent')}>
            {item.groupSlug || l10n('searchForm.groupSlugsMap', { slug: item.type })}
          </div>
        </Option>
      );
    }

    return (
      <Option
        key={`${item.code}_${i}`}
        ref={ref}
        className={cx('hover', 'option')}
        pureChildren={true}
        withArrow={false}
        withPadding={false}
        isSelected={isActive}
        isActive={isActive}
        isTouchDevice={isTouchDevice}
        onMouseDown={this.props.handleOptionFocus}
        onMouseEnter={this.props.handleActiveOptionSelect}
        value={item}
        onClick={this.props.handleOptionSelect}
        onBlur={this.props.handleOptionBlur}
        onFocus={this.props.handleOptionFocus}
        data-allow-touch-move={true}
      >
        {this.renderOptionContent(item)}
      </Option>
    );
  }

  renderOptions() {
    const { items } = this.props;
    return (items || []).map((item, i) => this.renderOption(item, i));
  }

  render() {
    const { isMobile, usePortalOverlay, childrenClassNames, children } = this.props;

    if (usePortalOverlay) {
      return (
        <div
          className={cx(
            'Options--portal',
            {
              'Options--isMobile': isMobile,
            },
            childrenClassNames.optionsContainer
          )}
          onBlur={this.handleBlur}
          ref={this.setOptionsContainerRef}
          data-locator="options"
        >
          {children}
          {this.renderOptions()}
        </div>
      );
    }

    return (
      <div
        className={cx(
          'Options',
          { 'Options--isMobile': isMobile },
          childrenClassNames.optionsContainer
        )}
        onBlur={this.handleBlur}
        ref={this.setOptionsContainerRef}
        data-locator="options"
      >
        {children}
        <PreventOutsideScroll>
          <div className={cx('scrollAreaWrap')} data-allow-touch-move={true}>
            <div className={cx('scrollArea')} data-locator="options-container">
              {this.renderOptions()}
            </div>
          </div>
        </PreventOutsideScroll>
      </div>
    );
  }
}

export default Options;
